import React, { useState, useEffect } from "react";
import { Award, Users, BookOpen, Star } from "lucide-react";
import { motion } from "framer-motion";

const About = () => {
  const [activeTab, setActiveTab] = useState("history");
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    setAnimated(true);
  }, []);

  const leadershipTeam = [
    {
      name: "Engr. Dr. Baba Aliyu Haruna",
      position: "Rector",
      image: {
        src: "/images/rector.jpg",
        alt: "Engr. Dr. Baba Aliyu Haruna",
      },
      bio: "Leading the institution with vision and dedication for over 2 years.",
    },
    {
      name: "Engr. Dr. A. A. Shehu",
      position: "Deputy Rector Academics",
      image: {
        src: "/images/AR.png",
        alt: "Engr. Dr. A. A. Shehu",
      },
      bio: "Expert in educational development with 15+ years of experience.",
    },
    {
      name: "Dr. Henry Koce",
      position: "Deputy Rector Administration",
      image: {
        src: "/images/henry.png",
        alt: "Dr.Henry Koce",
      },
      bio: "Passionate about fostering a supportive and inclusive campus environment.",
    },
    {
      name: "Surv. Elijah O. Alamu",
      position: "Deputy Rector Special Duties",
      image: {
        src: "/images/duty.png",
        alt: "Surv. Elijah O. Alamu",
      },
      bio: "Industry veteran with numerous publications in engineering sciences.",
    },
    {
      name: "Mallam Hussaini Muhammad",
      position: "Registrar",
      image: {
        src: "/images/registrar.png",
        alt: "Mallam Hussaini Muhammad",
      },
      bio: "Industry veteran with numerous publications in engineering sciences.",
    },
    {
      name: "Mallam Sadique Marufu ",
      position: "Burser",
      image: {
        src: "/images/bursar.png",
        alt: "Mallam Sadique Marufu",
      },
      bio: "Industry veteran with numerous publications in engineering sciences.",
    },
  ];

  const stats = [
    { value: "10000+", label: "Students", icon: <Users className="w-6 h-6" /> },
    { value: "200+", label: "Academic Board Members", icon: <BookOpen className="w-6 h-6" /> },
    { value: "40+", label: "Programs", icon: <Award className="w-6 h-6" /> },
    { value: "95%", label: "Employment Rate", icon: <Star className="w-6 h-6" /> },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* ===== HERO ===== */}
      <section
        className="relative min-h-[60vh] md:min-h-[70vh] bg-cover bg-center flex items-center justify-center"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)),  url('/images/slides.jpg')",
        }}
      >
        <div
          className={`text-center px-4 sm:px-6 lg:px-8 transition-all duration-1000 transform ${
            animated ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-white mb-4">
            About Federal Polytechnic Bida
          </h1>
          <p className="text-blue-100 text-base sm:text-lg max-w-2xl mx-auto">
            Excellence in technical education since 1985, shaping the future of
            innovation and industry in Nigeria.
          </p>
        </div>
      </section>

      {/* ===== STATS ===== */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
          {stats.map((stat, i) => (
            <div
              key={i}
              className={`text-center bg-blue-50 rounded-xl p-6 shadow-sm transition-all duration-700 ${
                animated
                  ? "translate-y-0 opacity-100"
                  : "translate-y-10 opacity-0"
              }`}
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              <div className="flex justify-center text-blue-600 mb-3">
                {stat.icon}
              </div>
              <div className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-blue-800">
                {stat.value}
              </div>
              <div className="text-gray-600 mt-2 text-sm">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== TABS ===== */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
            Our Story & Values
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto mb-10 text-sm sm:text-base">
            Discover what makes our institution unique and why we've been a
            leader in technical education for decades.
          </p>

          {/* Tab Nav */}
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {["history", "mission", "vision", "values"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-5 sm:px-6 py-2 sm:py-3 rounded-full font-medium capitalize transition-all shadow-sm ${
                  activeTab === tab
                    ? "bg-blue-700 text-white"
                    : "bg-white text-gray-700 hover:bg-blue-100"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-10 text-left max-w-5xl mx-auto transition-all duration-500">
            {activeTab === "history" && (
              <div className="space-y-6">
                <h3 className="text-xl sm:text-2xl font-bold mb-4">
                  Our Rich History
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
                  <div className="space-y-4">
                    <p className="text-justify text-gray-700 text-sm sm:text-base">
                      For over forty years, Federal Polytechnic Bida has grown to become one of the most respected and influential Polytechnics in Nigeria. Established in 1985, we have consistently provided quality technical education that meets the evolving needs of industry and society.
                    </p>
                    <p className="text-justify text-gray-700 text-sm sm:text-base">
                      The Polytechnic presently has seven schools and 31 departments offering 57 NBTE accredited programmes with eight new ones being proposed. Our growth from four schools and twenty-three academic departments to our current structure demonstrates our commitment to expansion and excellence.
                    </p>
                  </div>
                  <img
                    src="/images/bgim1.jpg"
                    alt="Campus History"
                    className="rounded-xl shadow-md w-full h-auto object-cover"
                  />
                </div>
                
                <div className="mt-8 bg-blue-50 p-6 rounded-xl">
                  <h4 className="font-semibold text-blue-800 mb-3">Our Academic Structure</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <h5 className="font-medium mb-2">Schools & Departments:</h5>
                      <ul className="list-disc list-inside space-y-1 text-gray-700">
                        <li>9 Schools</li>
                        <li>40+ Departments</li>
                        <li>40+ NBTE Accredited Programs</li>
                        <li>10+ New Programs Proposed</li>
                      </ul>
                    </div>
                    <div>
                      <h5 className="font-medium mb-2">Key Focus Areas:</h5>
                      <ul className="list-disc list-inside space-y-1 text-gray-700">
                        <li>Technical Education</li>
                        <li>Industry-Relevant Training</li>
                        <li>Research & Development</li>
                        <li>Community Service</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === "mission" && (
              <div className="space-y-6">
                <h3 className="text-xl sm:text-2xl font-bold mb-4">
                  Our Mission
                </h3>
                <p className="text-justify text-gray-700 text-sm sm:text-base mb-6">
                  To provide quality technical education that equips students with practical skills, theoretical knowledge, and ethical values necessary for professional excellence and societal development.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-blue-50 p-5 rounded-lg">
                    <h4 className="font-semibold text-blue-800 mb-3">Core Objectives</h4>
                    <ul className="space-y-2 text-gray-700 list-disc list-inside text-sm">
                      <li>Deliver industry-relevant curriculum and training</li>
                      <li>Foster innovation and critical thinking</li>
                      <li>Promote research and development activities</li>
                      <li>Develop well-rounded individuals with strong character</li>
                    </ul>
                  </div>
                  
                  <div className="bg-green-50 p-5 rounded-lg">
                    <h4 className="font-semibold text-green-800 mb-3">Our Commitment</h4>
                    <ul className="space-y-2 text-gray-700 list-disc list-inside text-sm">
                      <li>Student-centered learning environment</li>
                      <li>Continuous curriculum improvement</li>
                      <li>Industry partnerships for practical exposure</li>
                      <li>Community development initiatives</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === "vision" && (
              <div className="space-y-6">
                <h3 className="text-xl sm:text-2xl font-bold mb-4">
                  Our Vision
                </h3>
                <p className="text-justify text-gray-700 text-sm sm:text-base mb-6">
                  To be a world-class polytechnic renowned for excellence in technical education, innovation, and community service.
                </p>
                
                <div className="bg-purple-50 p-6 rounded-xl border-l-4 border-purple-500">
                  <p className="text-purple-800 italic text-lg text-justify">
                    "To create an institution where students don't just learn, but learn to transform the world through technology and innovation."
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3">Strategic Goals</h4>
                    <ul className="space-y-2 text-gray-700 list-disc list-inside text-sm">
                      <li>Become a center of excellence in technical education</li>
                      <li>Produce graduates who are industry-ready</li>
                      <li>Foster innovation and entrepreneurship</li>
                      <li>Enhance international collaboration and recognition</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3">Future Directions</h4>
                    <ul className="space-y-2 text-gray-700 list-disc list-inside text-sm">
                      <li>Expansion of digital learning resources</li>
                      <li>Development of new cutting-edge programs</li>
                      <li>Strengthening industry-academia partnerships</li>
                      <li>Enhanced research infrastructure</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === "values" && (
              <div className="space-y-6">
                <h3 className="text-xl sm:text-2xl font-bold mb-4">
                  Our Core Values
                </h3>
                <p className="text-justify text-gray-700 text-sm sm:text-base mb-6">
                  These fundamental principles guide our actions, decisions, and interactions within our academic community.
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {[
                    { 
                      title: "Excellence", 
                      desc: "We strive for the highest standards in all our endeavors, continuously improving our programs and services.",
                      color: "bg-blue-50",
                      textColor: "text-blue-800"
                    },
                    { 
                      title: "Integrity", 
                      desc: "We uphold honesty, ethics, and transparency in all actions and decisions, maintaining the trust of our stakeholders.",
                      color: "bg-green-50",
                      textColor: "text-green-800"
                    },
                    { 
                      title: "Innovation", 
                      desc: "We encourage creative thinking and novel solutions, embracing change and technological advancement.",
                      color: "bg-purple-50",
                      textColor: "text-purple-800"
                    },
                    { 
                      title: "Community", 
                      desc: "We serve and contribute positively to society, fostering inclusive growth and sustainable development.",
                      color: "bg-orange-50",
                      textColor: "text-orange-800"
                    }
                  ].map((v, i) => (
                    <div
                      key={i}
                      className={`${v.color} p-6 rounded-xl hover:shadow-md transition transform hover:-translate-y-1`}
                    >
                      <h4 className={`text-lg font-bold ${v.textColor} mb-2`}>
                        {v.title}
                      </h4>
                      <p className="text-gray-700 text-sm">
                        {v.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ===== LEADERSHIP ===== */}
      <section className="py-20 bg-white">
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
            Our Leadership
          </h2>
          <p className="text-gray-600 mb-10 max-w-2xl mx-auto text-sm sm:text-base">
            Meet the dedicated team guiding our institution toward excellence.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
            {leadershipTeam.map((m, i) => (
              <motion.div
                key={i}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.2 }}
                viewport={{ once: true }}
                className="bg-gray-50 rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500"
              >
                <div className="overflow-hidden">
                  <motion.img
                    src={m.image.src}
                    alt={m.image.alt}
                    className="w-full h-60 object-cover transform hover:scale-110 transition-transform duration-700 ease-in-out"
                    whileHover={{ scale: 1.1 }}
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg sm:text-xl font-bold">{m.name}</h3>
                  <p className="text-blue-700 font-medium">{m.position}</p>
                  <p className="text-gray-600 mt-3 text-sm sm:text-base">
                    {m.bio}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ===== CAMPUS TOUR ===== */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-indigo-800 text-white text-center px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">
            Experience Our Campus
          </h2>
          <p className="text-justify text-blue-100 text-sm sm:text-lg max-w-2xl mx-auto mb-6">
            Our state-of-the-art facilities provide the perfect environment for
            learning and innovation. Explore our beautiful campus and modern infrastructure.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://www.youtube.com/@TheFederalPolyBida" target="_blank" rel="noopener noreferrer">
              <button className="px-6 sm:px-8 py-3 sm:py-4 bg-white text-blue-800 font-semibold rounded-full shadow-md hover:bg-blue-100 transition">
                Take a Virtual Tour
              </button>
            </a>
            <button className="px-6 sm:px-8 py-3 sm:py-4 border-2 border-white text-white font-semibold rounded-full hover:bg-white hover:text-blue-800 transition">
              Schedule Campus Visit
            </button>
          </div>
        </div>
      </section>

      {/* ===== ACCREDITATION ===== */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
            Accreditations & Affiliations
          </h2>
          <p className="text-gray-600 mb-10 max-w-2xl mx-auto text-sm sm:text-base">
            We maintain the highest educational standards through our accreditations and partnerships with recognized bodies.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6 sm:gap-8">
            {["NBTE", "NUC", "COREN", "CITN", "NIS", "NIA"].map((org, index) => (
              <div
                key={index}
                className="h-24 sm:h-28 bg-gray-100 rounded-xl flex flex-col items-center justify-center p-4 hover:bg-blue-50 transition group"
              >
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mb-2 group-hover:bg-blue-700 transition">
                  {org.charAt(0)}
                </div>
                <span className="text-gray-600 font-medium text-sm group-hover:text-blue-700 transition">
                  {org}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;